import React from 'react';
import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader';
import { SFEntityID, SFNavigationOptions } from '../utils/types';
import css from './Header.scss';
import Navigation from '../navigation/Navigation';
import Sidebar from '../common/Sidebar/Sidebar';
import HeaderTitle from './HeaderTitle';
import NavigationButton from './Buttons/NavigationButton';
import LevelUpButton from './Buttons/LevelUpButton';
import CancelButton from './Buttons/CancelButton';
import SearchButton from './Buttons/SearchButton';
import StudentSearch from "./StudentSearch";
import {
  getHeaderTitle,
  getSectionTitle,
  shouldDisplayLevelUpButton,
  shouldDisplayCancelButton,
  parseURL
} from '../utils/header';

class Header extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      isNavigationVisible: false,
      isSearchVisible: false,
      title: getHeaderTitle(window.location.href, this.props.isStudent),
      currentSection: props.getCurrentSection()
    };

    // Reference: https://stackoverflow.com/questions/38564080/remove-event-listener-on-unmount-react
    this.onEscapePressed = this.onEscapePressed.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onHashChange = this.onHashChange.bind(this);

    // Monitor elements
    this.previouslyFocusedElement = null;
    this.currentFocusedElement = null;
    this.returnElement = null;
  }

  componentDidMount() {
    document.addEventListener("keydown", this.onEscapePressed, false);
    document.addEventListener("focus", this.onFocus, true);
    window.addEventListener("hashchange", this.onHashChange, false);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.onEscapePressed, false);
    document.removeEventListener("focus", this.onFocus, true);
    window.removeEventListener("hashchange", this.onHashChange, false);
  }

  onEscapePressed(e) {
    if (e.keyCode === 27) {
      if ( this.state.isNavigationVisible ) this.hideNavigationSidebar();
      else if (this.state.isSearchVisible) this.hideSearchSidebar();
    }
  }

  onFocus(e) {
    // If user clicked on the button in order to open the sidebar, we want to keep the previously focused
    // element and do not want to track focused elements in the sidebar
    this.previouslyFocusedElement = this.currentFocusedElement;
    this.currentFocusedElement = e.target;
  }

  onHashChange(e) {
    // If we are on a SPA, we need to monitor hash changes to update the title accordingly
    this.setState({title: getHeaderTitle(e.newURL, this.props.isStudent)});
  }

  // Store last focused element before opening the sidebar
  storeFocus() {
    this.returnElement = this.previouslyFocusedElement === null ? this.currentFocusedElement : this.previouslyFocusedElement;
  }

  // Return focus to element focused before opening the sidebar
  returnFocus() {
    if ( this.returnElement === null ) return;
    this.returnElement.focus();
  }

  showNavigationSidebar() {
    this.storeFocus();
    this.setState({isNavigationVisible: true});
  }

  hideNavigationSidebar() {
    this.setState({isNavigationVisible: false});
    this.returnFocus();
  }

  showSearchSidebar() {
    this.storeFocus();
    this.setState({isSearchVisible: true});
  }

  hideSearchSidebar() {
    this.setState({isSearchVisible: false});
    this.returnFocus();
  }

  updateSection() {
    this.hideNavigationSidebar();
    const currentSection = this.props.getCurrentSection();
    this.setState({currentSection});
    if ( currentSection ) this.setState({title: getSectionTitle(currentSection)});
  }

  get navigationSidebar() {
    return (
      <Sidebar visible={this.state.isNavigationVisible} onClose={this.hideNavigationSidebar.bind(this)} focusTrapActive={true}>
        <Navigation
          {...this.props}
          onNavigationDismissed={this.hideNavigationSidebar.bind(this)}
          updateSection={this.updateSection.bind(this)}
          currentSection={this.state.currentSection}
        />
      </Sidebar>
    );
  }

  get button() {
    const { hash } = parseURL(window.location.href);

    if ( shouldDisplayCancelButton(hash) ) return <CancelButton />;
    if ( shouldDisplayLevelUpButton(hash) ) return <LevelUpButton focusRef={this.props.isFocusFlag}/>;
    return <NavigationButton onClick={this.showNavigationSidebar.bind(this)} isExpanded ={this.state.isNavigationVisible} />;
  }

  get searchSidebar() {
    if (!this.props.showStudentSearch || !this.props.isMobile) return null;
    return (
      <Sidebar direction="top" visible={this.state.isSearchVisible} onClose={this.hideSearchSidebar.bind(this)} focusTrapActive={false}>
        <StudentSearch isTopbar={true} getStudentSearch={this.props.getStudentSearch} />
      </Sidebar>
    );
  }

  get studentSearch() {
    if ( !this.props.showStudentSearch ) return null;
    if ( this.props.isMobile ) {
      // Do not show student search when custom button is displayed
      const { hash } = parseURL(window.location.href);
      return shouldDisplayLevelUpButton(hash) || shouldDisplayCancelButton(hash)
        ? null
        : <SearchButton onClick={this.showSearchSidebar.bind(this)} />;
    }
    return <StudentSearch getStudentSearch={this.props.getStudentSearch} />;
  }

  render() {
    return (
      <div>
        <div className={css['header-container']} style={{'backgroundColor': this.props.headerColor}}>
          <div className={css['header-title-container']} role="navigation">
            {this.button}
            <HeaderTitle isMobile={this.props.isMobile} title={this.state.title} />
          </div>
          {this.studentSearch}
        </div>
        {this.searchSidebar}
        {this.navigationSidebar}
      </div>
    );
  }
}

Header.propTypes = {
  navigationOptions: SFNavigationOptions.isRequired,
  isMobile: PropTypes.bool,
  showStudentSearch: PropTypes.bool,
  getStudentSearch: PropTypes.func,
  getCurrentSection: PropTypes.func.isRequired,
  userId: SFEntityID.isRequired,
  username: PropTypes.string.isRequired,
  photoURL: PropTypes.string.isRequired,
  isStudent: PropTypes.bool.isRequired,
  headerColor: PropTypes.string
};

export default hot(module)(Header);
